import type { MutableRefObject } from 'react'
import type { TranscriptItem } from 'types'
import type { AgentMetadata } from '../core/agents/types.js'
import { DISCOVERY_REPORT_ACK_TEXT } from '../../utils/discoveryReportUtils.js'
import { buildContextSources } from './context-sources.js'
import { createBookingWidgetAttachment } from './transcript-side-effects.js'
import { generateTranscriptId } from './transcript-append.js'

export interface OrchestratorTurnAgentResponse {
  output: string
  agent: string
  metadata?: AgentMetadata | Record<string, unknown>
}

export interface ApplyOrchestratorTurnDeps {
  clientSessionViewStore: {
    getSnapshot: () => {
      intelligenceContext?: Record<string, unknown> | object
      transcript: TranscriptItem[]
    }
  }
  transcriptRef: MutableRefObject<TranscriptItem[]>
  currentHistory: TranscriptItem[]
  loadingId?: string
  turnId?: string
  finalReasoning?: string
  tools?: TranscriptItem['tools']
  preserveExistingModelText?: boolean
}

export interface ApplyOrchestratorTurnResult {
  transcript: TranscriptItem[]
  backendStatus: {
    mode: 'agents'
    message: string
    severity: 'info'
  }
  triggerBooking: boolean
  triggerDiscoveryReport: boolean
  summaryAgentOutput?: SummaryAgentOutput
}

export interface SummaryAgentOutput {
  executiveSummary?: string
  keyFindings?: unknown
  recommendedSolution?: string
  solutionRationale?: string
  expectedROI?: string
  pricingBallpark?: string
  nextSteps?: string
  multimodalInteractionSummary?: unknown
}

export function parseSummaryAgentOutput(output: string | undefined): SummaryAgentOutput | undefined {
  if (!output?.trim()) return undefined
  try {
    const parsed = JSON.parse(output) as Record<string, unknown>
    if (!parsed || typeof parsed !== 'object') return undefined
    const summary: SummaryAgentOutput = {}
    if (typeof parsed.executiveSummary === 'string') summary.executiveSummary = parsed.executiveSummary
    if (parsed.keyFindings !== undefined) summary.keyFindings = parsed.keyFindings
    if (typeof parsed.recommendedSolution === 'string') {
      summary.recommendedSolution = parsed.recommendedSolution
    }
    if (typeof parsed.solutionRationale === 'string') {
      summary.solutionRationale = parsed.solutionRationale
    }
    if (typeof parsed.expectedROI === 'string') summary.expectedROI = parsed.expectedROI
    if (typeof parsed.pricingBallpark === 'string') summary.pricingBallpark = parsed.pricingBallpark
    if (typeof parsed.nextSteps === 'string') summary.nextSteps = parsed.nextSteps
    if (parsed.multimodalInteractionSummary !== undefined) {
      summary.multimodalInteractionSummary = parsed.multimodalInteractionSummary
    }
    return summary
  } catch {
    return undefined
  }
}

export function applyOrchestratorTurnResult(
  agentResponse: OrchestratorTurnAgentResponse,
  deps: ApplyOrchestratorTurnDeps
): ApplyOrchestratorTurnResult {
  const metadata = (agentResponse.metadata || {})

  const groundingMetadata = metadata.groundingMetadata as Record<string, unknown> | undefined
  const chainOfThought = metadata.chainOfThought as TranscriptItem['chainOfThought']
  const triggerBooking =
    metadata.triggerBooking === true || metadata.action === 'show_calendar_widget'
  const triggerDiscoveryReport =
    metadata.triggerDiscoveryReport === true || metadata.action === 'generate_discovery_report'
  const proposal = metadata.proposal
  const proposalAttachment =
    proposal && typeof proposal === 'object'
      ? {
          type: 'proposal' as const,
          name: 'Proposal draft',
          data: JSON.stringify(proposal),
        }
      : undefined

  const webSources =
    (groundingMetadata?.groundingChunks as Array<Record<string, unknown>> | undefined)
      ?.filter(c => c?.web || c?.retrievedUri)
      ?.map(c => ({
        title:
          (c.web as { title?: string } | undefined)?.title ||
          (c.title as string | undefined) ||
          'Source',
        url:
          (c.web as { uri?: string } | undefined)?.uri ||
          (c.retrievedUri as string | undefined) ||
          '#',
      }))
      ?.filter((s): s is { title: string; url: string } => Boolean(s.url && s.url !== '#')) ?? []

  const snapshotIntel = deps.clientSessionViewStore.getSnapshot().intelligenceContext as
    | Record<string, unknown>
    | undefined
  const canonicalLocation = snapshotIntel?.location as
    | { city?: string; country?: string }
    | undefined

  const contextSources = buildContextSources({
    ...(snapshotIntel?.company
      ? {
          company: snapshotIntel.company as {
            name?: string
            domain?: string
          },
        }
      : {}),
    ...(snapshotIntel?.person
      ? {
          person: snapshotIntel.person as {
            fullName?: string
            role?: string
          },
        }
      : {}),
    ...(canonicalLocation?.city
      ? {
          location: {
            city: canonicalLocation.city,
            ...(canonicalLocation.country ? { country: canonicalLocation.country } : {}),
          },
        }
      : {}),
    hasConversation: deps.currentHistory.length > 1,
    ...(webSources.length > 0 ? { webSources } : {}),
  })

  const loadingId = deps.loadingId || generateTranscriptId('assistant')
  const updated = [...deps.transcriptRef.current]
  const idx = updated.findIndex(item => item.id === loadingId)
  const base = idx >= 0 && updated[idx] ? updated[idx] : undefined

  const finalItem: TranscriptItem = {
    id: loadingId,
    role: 'model',
    text:
      deps.preserveExistingModelText && base?.text?.trim()
        ? base.text
        : triggerDiscoveryReport
          ? DISCOVERY_REPORT_ACK_TEXT
          : agentResponse.output || base?.text || '',
    timestamp: base?.timestamp || new Date(),
    ...(deps.turnId ? { turnId: deps.turnId } : base?.turnId ? { turnId: base.turnId } : {}),
    isFinal: true,
    status: 'complete',
    isLiveTranscript: false,
    ...(chainOfThought?.steps &&
    Array.isArray(chainOfThought.steps) &&
    chainOfThought.steps.length > 0
      ? { chainOfThought }
      : {}),
    ...(deps.finalReasoning ? { reasoning: deps.finalReasoning } : {}),
    ...(groundingMetadata
      ? {
          groundingMetadata: groundingMetadata as unknown as TranscriptItem['groundingMetadata'],
        }
      : {}),
    ...(contextSources.length > 0 ? { contextSources } : {}),
    ...(deps.tools && deps.tools.length > 0 ? { tools: deps.tools } : {}),
    ...(triggerBooking ? { attachment: createBookingWidgetAttachment() } : {}),
    ...(!triggerBooking && proposalAttachment ? { attachment: proposalAttachment } : {}),
  }

  if (idx >= 0) updated[idx] = finalItem
  else updated.push(finalItem)

  const result: ApplyOrchestratorTurnResult = {
    transcript: updated,
    backendStatus: {
      mode: 'agents',
      message: `Agent: ${agentResponse.agent}`,
      severity: 'info',
    },
    triggerBooking,
    triggerDiscoveryReport,
  }
  if (triggerDiscoveryReport) {
    const summaryAgentOutput = parseSummaryAgentOutput(agentResponse.output)
    if (summaryAgentOutput) result.summaryAgentOutput = summaryAgentOutput
  }
  return result
}

export function consumeOrchestratorTriggerMetadata(metadata: Record<string, unknown> | undefined): void {
  if (!metadata) return
  delete metadata.triggerBooking
  delete metadata.triggerDiscoveryReport
  if (metadata.action === 'show_calendar_widget') {
    metadata.action = 'calendar_widget_shown'
  }
  if (metadata.action === 'generate_discovery_report') {
    metadata.action = 'discovery_report_generating'
  }
}
